import { resolveAccessToFeature } from '@/utils/navigationData'
import { until } from '@vueuse/core'
import * as Sentry from '@sentry/browser'
import store from '@/store'
import { authBus, AuthActions } from '@/services/eventbus.service'
import { logger } from '@/utils/logger'

/**
 * Error codes for access denial - shown on /oops page
 * E1xx = Timeout errors, E2xx = Data errors, E3xx = Permission errors
 */
export const ACCESS_ERROR = {
  E101: 'E101',
  E102: 'E102',
  E201: 'E201',
  E202: 'E202',
  E301: 'E301',
  E999: 'E999'
}

// Store last error for display on /oops page
let lastAccessError = null
const ERROR_TTL = 5 * 60 * 1000 // 5 minutes

export const getLastAccessError = () => {
  if (lastAccessError && Date.now() - lastAccessError.time > ERROR_TTL) {
    lastAccessError = null
  }
  return lastAccessError
}

export const clearLastAccessError = () => {
  lastAccessError = null
}

/**
 * Timeouts (ms)
 */
const TIMEOUTS = { boards: 20000, features: 20000, totalRetry: 30000 }

/**
 * Log and store access error
 */
const setError = (code, context = {}) => {
  if (!code) return null

  lastAccessError = { code, ...context, time: Date.now() }

  // Console log in dev
  if (import.meta.env.MODE === 'development') {
    logger.warn('[Access]', code, context)
  }

  // Report transient errors to Sentry
  if (code !== ACCESS_ERROR.E301) {
    Sentry?.captureMessage(`Access error: ${code}`, {
      level: 'warning',
      tags: { code },
      extra: context
    })
  }

  return code
}

/**
 * Wait for condition with timeout
 */
const waitFor = async (conditionFn, timeoutMs) => {
  let timedOut = false
  await Promise.race([
    until(conditionFn).toMatch(Boolean),
    new Promise((r) =>
      setTimeout(() => {
        timedOut = true
        r()
      }, timeoutMs)
    )
  ])
  return !timedOut
}

/**
 * Feature access service
 */
const featureAccessService = {
  successCache: new Map(), // For fallback during transient failures

  /**
   * Clear all caches - call on logout to prevent data leakage
   */
  clearCache() {
    this.successCache.clear()
    store.commit('setFeatureAccessCache', null)
    lastAccessError = null
  },

  /**
   * Check feature access with retries and fallback
   */
  async checkFeatureAccess({ boardId, feature }) {
    if (!boardId || !feature) return true

    const key = `${boardId}:${feature}`

    // Check store cache (single source of truth)
    const storeCache = store.getters.featureAccessCache
    if (storeCache?.has(key)) return storeCache.get(key)

    try {
      const result = await this._checkWithRetry(boardId, feature, key)

      // Cache definitive results in store
      if (result.definitive) {
        const currentCache = store.getters.featureAccessCache || new Map()
        const updated = new Map(currentCache)
        updated.set(key, result.hasAccess)
        store.commit('setFeatureAccessCache', updated)
      }

      return result.hasAccess
    } catch (error) {
      // Only set E999 if no error was already recorded by inner functions
      if (!lastAccessError) {
        setError(ACCESS_ERROR.E999, { boardId, feature, error: error.message })
      }
      return this._fallbackOrDeny(key)
    }
  },

  /**
   * Check with retry logic - wrapped with overall timeout to prevent long hangs
   */
  async _checkWithRetry(boardId, feature, key) {
    let timeoutId

    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        resolve({ hasAccess: this._fallbackOrDeny(key), definitive: false, timedOut: true })
      }, TIMEOUTS.totalRetry)
    })

    const retryPromise = this._doRetryLoop(boardId, feature, key)

    try {
      const result = await Promise.race([retryPromise, timeoutPromise])

      // If overall timeout hit, set error
      if (result.timedOut) {
        setError(ACCESS_ERROR.E101, { boardId, feature, reason: 'Overall retry timeout exceeded' })
      }

      return result
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
    }
  },

  /**
   * Internal retry loop - extracted to allow overall timeout wrapper
   */
  async _doRetryLoop(boardId, feature, key) {
    let lastErrorCode = null

    for (let attempt = 0; attempt < 3; attempt++) {
      const result = await this._doCheck(boardId, feature)

      // Success - cache and return
      if (result.hasAccess) {
        this.successCache.set(key, Date.now())
        return { hasAccess: true, definitive: true }
      }

      // Track error code for this attempt
      lastErrorCode = result.code

      // Permission denied (E301) or invalid params (E202) - record error and return immediately
      // These are definitive failures that won't resolve with retries
      if (result.code === ACCESS_ERROR.E301 || result.code === ACCESS_ERROR.E202) {
        setError(result.code, { boardId, feature })
        return { hasAccess: false, definitive: true }
      }

      // Transient failure - retry with backoff
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)))
      }
    }

    // All retries failed - record error and try fallback
    setError(lastErrorCode, { boardId, feature })
    return { hasAccess: this._fallbackOrDeny(key), definitive: false }
  },

  /**
   * Core access check
   */
  async _doCheck(boardId, feature) {
    if (!boardId) {
      return { hasAccess: false, code: ACCESS_ERROR.E202 }
    }

    // Ensure boards are loaded
    if (!store.getters.role(boardId)) {
      if (!store.getters.mainBoards?.length) {
        const user = store.getters.user
        if (user?.uid) await store.dispatch('fetchAllBoards', user.uid)

        const boardsLoaded = await waitFor(
          () => store.getters.mainBoards?.length > 0,
          TIMEOUTS.boards
        )
        if (!boardsLoaded) {
          return { hasAccess: false, code: ACCESS_ERROR.E101 }
        }
      }
    }

    const role = store.getters.role(boardId)
    const featuresFetched = store.getters['accessScopes/featuresFetched']
    const loading = store.getters['accessScopes/featuresLoading']

    // Fetch features if needed
    if (featuresFetched !== boardId && !loading) {
      await store.dispatch('accessScopes/fetchFeatures', { boardId })

      if (
        !(await waitFor(
          () => store.getters['accessScopes/featuresFetched'] === boardId,
          TIMEOUTS.features
        ))
      ) {
        return { hasAccess: false, code: ACCESS_ERROR.E102 }
      }
    } else if (loading) {
      if (
        !(await waitFor(() => !store.getters['accessScopes/featuresLoading'], TIMEOUTS.features))
      ) {
        return { hasAccess: false, code: ACCESS_ERROR.E102 }
      }
      // After loading completes, check if it was for a different board
      if (store.getters['accessScopes/featuresFetched'] !== boardId) {
        await store.dispatch('accessScopes/fetchFeatures', { boardId })
        if (
          !(await waitFor(
            () => store.getters['accessScopes/featuresFetched'] === boardId,
            TIMEOUTS.features
          ))
        ) {
          return { hasAccess: false, code: ACCESS_ERROR.E102 }
        }
      }
    }

    const features = store.getters['accessScopes/features']
    if (store.getters['accessScopes/featuresFetched'] !== boardId) {
      return { hasAccess: false, code: ACCESS_ERROR.E201 }
    }

    const hasAccess = resolveAccessToFeature({
      feature,
      accessScopesFeatures: features,
      role
    })

    return { hasAccess, code: hasAccess ? null : ACCESS_ERROR.E301 }
  },

  /**
   * Fallback to cached success or deny
   */
  _fallbackOrDeny(key) {
    const lastSuccess = this.successCache.get(key)
    return Boolean(lastSuccess && Date.now() - lastSuccess < 5 * 60 * 1000)
  }
}

// Clear caches on logout to prevent data leakage
// Note: This listener is registered once at module load. While it persists in
// hot-reload scenarios, it's idempotent (always calls the same singleton method).
authBus.on(AuthActions.Logout, () => {
  featureAccessService.clearCache()
})

export { featureAccessService }
